import { useCallback, useEffect, useRef, useState } from "react";

export type LeadSaveState =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "saved"; at: Date }
  | { kind: "failed"; message: string };

// Autosave. Save and Save & next are gone, so edits persist on their own:
// about a second after the operator stops changing things, and again when
// they leave the row.
//
// The panel's indicator is not decoration - without a button, a failed write
// has nothing to show for itself, so a silent failure would be invisible.
// It is also what the navigation blocker keys on: blocking on `dirty` (what
// the manual-Save flow did) would prompt on every row the operator merely
// touches, which is worse than the button it replaces. Only a save that
// actually FAILED holds them up.
//
// Everything is passed by VALUE, never as a getter: the caller computes its
// payload above the hook call, and a getter closing over a `const` below it
// would throw a temporal-dead-zone ReferenceError the moment this hook read it
// during the caller's render.
export const useLeadAutosave = <TPayload>({
  dirty,
  payload,
  disabled,
  onSave,
  onFlushFailed,
}: {
  dirty: boolean;
  payload: TPayload;
  // No write of its own on this surface (the linked-site mode disables Save),
  // so autosave stays off rather than writing selections it treats as
  // read-only.
  disabled: boolean;
  onSave: (payload: TPayload) => Promise<void>;
  // The unmount flush failed. The panel is gone by the time the rejection
  // lands, so the owner is the only place a failed save can still be told to
  // the operator.
  onFlushFailed: (message: string) => void;
}): {
  saveState: LeadSaveState;
  runAutosave: (payload: TPayload) => Promise<void>;
  resetSaveState: () => void;
} => {
  const [saveState, setSaveState] = useState<LeadSaveState>({ kind: "idle" });
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const onFlushFailedRef = useRef(onFlushFailed);
  onFlushFailedRef.current = onFlushFailed;
  // Read by the leave-the-row flush, which runs in a cleanup and so cannot
  // depend on the render's closure being current.
  const autosaveRef = useRef({ dirty, payload });
  autosaveRef.current = { dirty, payload };
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  const runAutosave = useCallback(
    async (toSave: TPayload) => {
      setSaveState({ kind: "saving" });
      try {
        await onSave(toSave);
        setSaveState({ kind: "saved", at: new Date() });
      } catch (e) {
        setSaveState({
          kind: "failed",
          message: e instanceof Error ? e.message : "Save failed",
        });
      }
    },
    [onSave]
  );

  useEffect(() => {
    if (!dirty || disabled) return;
    const timer = setTimeout(() => void runAutosave(payload), 1000);
    return () => clearTimeout(timer);
  }, [dirty, payload, disabled, runAutosave]);

  useEffect(
    () => () => {
      // Leaving the row: flush whatever the debounce has not yet written. Not
      // awaited - the component is going away - but the request is already in
      // flight by then, which is what stops a quick row-switch losing the edit.
      //
      // The rejection has to be caught HERE, not left to `runAutosave`: this
      // path bypasses it, and there is no component left to render a failed
      // `saveState`. So the owner is told instead, which is the only surface
      // that outlives the unmount - without it a save that fails during a fast
      // row switch is lost with no error anywhere.
      //
      // Runs on unmount ONLY, reading everything through refs. It used to key on
      // `disabled`, so a linked site appearing mid-session (the operator
      // created one from this panel) ran this cleanup too and wrote a stale
      // payload for a row that had just become read-only.
      const { dirty: stillDirty, payload: last } = autosaveRef.current;
      if (stillDirty && !disabledRef.current) {
        const notifyFailure = onFlushFailedRef.current;
        void onSaveRef.current(last).catch((e: unknown) => {
          notifyFailure(e instanceof Error ? e.message : "Save failed");
        });
      }
    },
    []
  );

  const resetSaveState = useCallback(() => setSaveState({ kind: "idle" }), []);

  return { saveState, runAutosave, resetSaveState };
};
